<template>
  <VApp :dark="isDark" class="v-app base">
    <NotificationCenter />
    <KmTooltipPortal />
    <template>
      <!-- Global components -->
      <SupportPopup />
      <NavigationPortal />

      <!-- PM2 Plus popup pricing -->
      <KmModal
        v-model="activatePM2"
        noClickAnimation
        persistent
        maxWidth="400"
        contentClass="payment-modal"
      >
        <PricingTable :upgrade="true" :handleSubscribe="subscribe" />
      </KmModal>

      <!-- Card registration popup -->
      <UpgradeStepper
        v-if="canUpgrade && $store.getters.currentBucket"
        :isOpen="upgradeStepperOpen"
        :plan="plan"
        :isTrial="isTrial"
        :handleBack="closeStepper"
        @close="upgradeStepperOpen = false"
        @back="activatePM2 = true"
      />

      <!-- Main Page content -->
      <div class="app-content flex-col flex-1">
        <AnnouncementBanner />
        <Header
          :mobileNavOpen="isMobileNavOpen"
          :haveNavBar="haveNavBar"
          @toggleSidebar="toggleMobileNav"
        />
        <div class="flex-row flex-1">
          <BucketNavBar
            v-if="haveNavBar"
            :mobileOpen="isMobileNavOpen"
            @update:mobileOpen="isMobileNavOpen = $event"
          />
          <div class="flex-1 flex-col page-top-and-content">
            <PageTop />
            <RouterView v-if="!isPageLoading" />
            <div v-else class="route-loader">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </div>
    </template>
  </VApp>
</template>

<script>
import themes from '@/config/themes'
import Color from '@/utils/Color'
import { registerRequestCallback, registerResponseCallback } from '@/utils/pm2Io'
import * as act from '@/store/actions'
import * as mut from '@/store/mutations'

import { DISABLED_BUCKET_PAGE } from '@/router'
import AnnouncementBanner from './AnnouncementBanner'
import BucketNavBar from './BucketNavBar'
import Header from './Header'
import LoadingSpinner from '@/components/LoadingSpinner'
import PricingTable from '@/components/pricing/PricingTable'
import UpgradeStepper from '@/components/pricing/UpgradeStepper'
import NavigationPortal from './NavigationPortal'
import NotificationCenter from './NotificationCenter'
import { Portal as KmTooltipPortal } from '@/components/KmTooltip'

import stripe from '@/utils/stripe'

import 'highlight.js/styles/atom-one-dark.css'
import KmModal from '@/components/KmModal'
import SupportPopup from './SupportPopup'
import PageTop from './PageTop'

// TODO: bundle material icons css directly instead of loading them from style tag
export default {
  name: 'App',
  components: {
    NotificationCenter,
    AnnouncementBanner,
    BucketNavBar,
    Header,
    LoadingSpinner,
    PricingTable,
    KmModal,
    UpgradeStepper,
    NavigationPortal,
    KmTooltipPortal,
    SupportPopup,
    PageTop,
  },
  data() {
    return {
      currentRoute: '',
      plan: {},
      isTrial: false,
      upgradeStepperOpen: false,
      isMobileNavOpen: false,
    }
  },

  computed: {
    haveNavBar() {
      return this.$route.params.id !== undefined && Boolean(this.$route.params.appType)
    },
    isDark() {
      return this.$store.state.theme === 'dark'
    },
    areCriticalResourcesFetched() {
      return this.$store.getters.areCriticalResourcesFetched
    },
    isPageLoading() {
      return this.$store.getters.isPageLoading
    },
    activatePM2: {
      get() {
        return this.$store.getters.activatePM2.isOpen
      },
      set(value) {
        this.$store.commit(mut.SET_ACTIVATE_PM2, { opened: value })
      },
    },
    canUpgrade() {
      return this.$store.getters.isUserOwner && !this.$store.getters.isDedicated
    },
    user() {
      return this.$store.getters.user
    },
    currentBucketId() {
      return this.$store.getters.currentBucketId
    },
    currentBucket() {
      return this.$store.getters.currentBucket
    },
    currentPlan() {
      return this.$store.getters.currentPlan
    },
  },
  watch: {
    currentBucket: {
      handler(next) {
        let route_name = this.$router.currentRoute.name
        if (
          next &&
          next.bucket &&
          route_name != 'BILLING_CARDS_PAGE' &&
          route_name != 'BILLING_SEPA_PAGE' &&
          route_name != 'DISABLED_BUCKET_PAGE'
        ) {
          let bucket = next.bucket
          // Re route to blocked page if "Show warning" is selected in backoffice
          if (
            bucket.configuration &&
            bucket.configuration.ui_options &&
            bucket.configuration.ui_options.warning
          ) {
            this.$router.push({
              name: DISABLED_BUCKET_PAGE,
              params: {
                id: bucket._id,
              },
            })
          }
        }
      },
    },
    areCriticalResourcesFetched: {
      handler(next) {
        if (next) {
          if (
            !Object.values(this.$store.state.buckets).length ||
            document.location.search.includes('on-boarding-wizard')
          ) {
            this.$store.commit(mut.SET_ON_BOARDING, { isOpen: true })
          }
        }
      },
      immediate: true,
    },
  },
  created() {
    window.vuetify = this.$vuetify
    this.upgradeStepperOpen = false
    this.setupTheme()
  },
  methods: {
    toggleMobileNav() {
      if (!this.$vuetify.breakpoint.mdAndUp) {
        this.isMobileNavOpen = !this.isMobileNavOpen
      }
    },
    subscribe(plan, isTrial) {
      this.plan = plan
      this.isTrial = isTrial
      this.activatePM2 = false
      this.upgradeStepperOpen = true
    },
    closeStepper() {
      window.location.reload()
      this.upgradeStepperOpen = false
    },
    setupTheme() {
      // Set the dynamic theme props
      const themeName = this.$store.state.theme
      const mainColor = themes._base.brand
      const theme = {
        ...themes._base,
        ...themes[themeName],
        primary: mainColor,
        accent: mainColor,
      }
      Object.keys(theme).forEach((key) => {
        document.body.style.setProperty(`--${key}`, theme[key])
        const color = Color.from(theme[key])
        document.body.style.setProperty(
          `--${key}-fade`,
          themeName === 'dark' ? color.darken(0.1) : color.lighten(0.1),
        )
        document.body.style.setProperty(
          `--${key}-accentuate`,
          themeName === 'dark' ? color.lighten(0.1) : color.darken(0.1),
        )
      })
      // Vuetify generates a global `.<key> { background-color !important }` class per
      // theme key: 'border' would hijack the `border` radius utility class used on page
      // boxes (contrast pa-3 border), so expose it as a CSS variable only
      const vuetifyTheme = { ...theme }
      delete vuetifyTheme.border
      this.$vuetify.theme = vuetifyTheme
    },
  },
}
</script>

<style lang="postcss" scoped>
#app {
  font-family: var(--font-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
}

.app-content {
  display: flex;
  flex-direction: column;
  min-height: 0;

  & .page-top-and-content {
    min-width: 0;
  }
}

.main-loader {
  flex: 1;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.row-container {
  display: flex;
  flex-direction: row;
  position: relative;
  min-height: calc(100vh - 1 * var(--headerHeight));
}

.route-loader {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.container {
  display: flex;
}

.content {
  flex: 1;
}
.flex-row {
  min-height: 0;
}
</style>
